import {
  UsdMicros,
  zUsdCents,
  zUsdDollars,
  zUsdMicros,
} from "@redotech/money/usd-amounts";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DEFAULT_EXCHANGE_AGENT_USAGE_FEE } from "./team-billing";

export enum BillingType {
  MARKETING_SMS = "marketing-sms",
  MARKETING_EMAIL = "marketing-email",
  ORDER_TRACKING = "order-tracking",
  RETURN_AI_EXCHANGE = "return-ai-exchange",
  RETURNS = "returns",
  CHECKOUT_OPTIMIZATION = "checkout-optimization",
  CHECKOUT_OPTIMIZATION_PER_ORDER = "checkout-optimization-per-order",
  AEO = "aeo",
  IMS = "ims",
  IMS_FORECASTING = "ims-forecasting",
  RECOVER = "recover",
  DISPUTES = "disputes",
  DISPUTES_ALERTS_DEFLECTIONS = "disputes-alerts-deflections",
  LCG_COMMIT = "lcg-commit",
  CTX_USAGE = "ctx-usage",
  SUPPORT_AI_USAGE = "support-ai-usage",
  DIMENSIONAL_PACKAGING = "dimensional-packaging",
  WMS = "wms",
  OMS = "oms",
}

const baseBillingWindowSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  type: createEnum(BillingType, { enumName: "BillingType" }),
});
export type CreateBillingWindow<
  T extends z.infer<typeof baseBillingWindowSchema>,
> = Omit<T, "_id" | "createdAt" | "updatedAt">;

export type MarketingSmsBillingWindow = z.infer<
  typeof marketingSmsBillingWindowSchema
>;
export const marketingSmsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.MARKETING_SMS),

  // Credits and pricing for SMS/MMS
  includedSmsCredits: z.number(),
  overageSmsPriceMicros: zUsdMicros,
  overageMmsPriceMicros: zUsdMicros,

  ignoreCarrierFees: z.boolean(),
});

export type MarketingEmailBillingWindow = z.infer<
  typeof marketingEmailBillingWindowSchema
>;
export const marketingEmailBillingWindowSchema = baseBillingWindowSchema.extend(
  {
    type: z.literal(BillingType.MARKETING_EMAIL),

    // Credits and pricing for Email
    includedEmailCredits: z.number(),
    overagePriceMicros: zUsdMicros,
  },
);

export type OrderTrackingBillingWindow = z.infer<
  typeof orderTrackingBillingWindowSchema
>;
export const orderTrackingBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.ORDER_TRACKING),

  // Flags
  billPerOrderEnabled: z.boolean(),
  billPerShipmentEnabled: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false),
  billPerSmsEnabled: z.boolean(),
  billPerEmailEnabled: z.boolean(),

  // Credits and pricing for Order Tracking
  includedOrders: z.number(),
  overagePricePerOrderMicros: zUsdMicros,

  includedShipments: z
    .number()
    .nullish()
    .transform((value) => value ?? (0 as UsdMicros)),
  overagePricePerShipmentMicros: zUsdMicros
    .nullish()
    .transform((value) => value ?? (0 as UsdMicros)),

  includedSmsCredits: z.number(),
  overagePricePerSmsMicros: zUsdMicros,
  overagePricePerMmsMicros: zUsdMicros,
  ignoreCarrierFees: z.boolean(),

  includedEmailCredits: z.number(),
  overagePricePerEmailMicros: zUsdMicros,
});

export const exchangeAgentBillingFeeType = ["flat", "percentage"] as const;

export type ReturnAiExchangeBillingWindow = z.infer<
  typeof returnAiExchangeBillingWindowSchema
>;
export const returnAiExchangeBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.RETURN_AI_EXCHANGE),

    // Pricing snapshot when billing window was created
    // Optional with default for backward compatibility with existing documents
    usageFeeAtCreation: zUsdDollars
      .nullish()
      .default(DEFAULT_EXCHANGE_AGENT_USAGE_FEE as z.infer<typeof zUsdDollars>),
    // Percentage fee snapshot when billing window was created
    // Optional with default for backward compatibility with existing documents
    usageFeePercentAtCreation: z.number().nullish().default(0),
    // Billing fee type snapshot when billing window was created
    // Optional with default for backward compatibility with existing documents
    billingFeeTypeAtCreation: z
      .enum(exchangeAgentBillingFeeType)
      .nullish()
      .default("flat"),
  });

export type ReturnsBillingWindow = z.infer<typeof returnsBillingWindowSchema>;
export const returnsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.RETURNS),
});

export type AeoBillingWindow = z.infer<typeof aeoBillingWindowSchema>;
export const aeoBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.AEO),
});

export type CtxUsageBillingWindow = z.infer<typeof ctxUsageBillingWindowSchema>;
export const ctxUsageBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.CTX_USAGE),
});

export type WmsBillingWindow = z.infer<typeof wmsBillingWindowSchema>;
export const wmsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.WMS),
});

export type OmsBillingWindow = z.infer<typeof omsBillingWindowSchema>;
export const omsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.OMS),
});

export type SupportAiUsageBillingWindow = z.infer<
  typeof supportAiUsageBillingWindowSchema
>;
export const supportAiUsageBillingWindowSchema = baseBillingWindowSchema.extend(
  { type: z.literal(BillingType.SUPPORT_AI_USAGE) },
);

export type DimensionalPackagingBillingWindow = z.infer<
  typeof dimensionalPackagingBillingWindowSchema
>;
export const dimensionalPackagingBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.DIMENSIONAL_PACKAGING),
  });

export type ImsBillingWindow = z.infer<typeof imsBillingWindowSchema>;
export const imsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.IMS),
});

export type ImsForecastingBillingWindow = z.infer<
  typeof imsForecastingBillingWindowSchema
>;
export const imsForecastingBillingWindowSchema = baseBillingWindowSchema.extend(
  { type: z.literal(BillingType.IMS_FORECASTING) },
);

export const checkoutOptimizationBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.CHECKOUT_OPTIMIZATION),
    pricePerOrderInCents: zUsdCents,
  });
export type CheckoutOptimizationBillingWindow = z.infer<
  typeof checkoutOptimizationBillingWindowSchema
>;

/**
 * Per-period state for engine-routed CO per-order billing. Replaces the
 * legacy on-invoice counter (`service_invoice.billableOrdersCreated`).
 * `bill-for-order.ts` increments the counters here as orders cross
 * checkout; the engine reads them at period close.
 */
export const checkoutOptimizationPerOrderBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.CHECKOUT_OPTIMIZATION_PER_ORDER),
    pricePerOrderInCents: zUsdCents,
    /** Snapshot of orders that became billable in this window. */
    billableOrdersCreated: z.number().int().min(0).default(0),
    /** Subset of billable orders that picked a Redo-influenced shipping line. */
    ordersCreatedWithRedoShippingMethod: z.number().int().min(0).default(0),
    /** Subset billed solely because intent ran live on the cart. */
    ordersBilledForIntent: z.number().int().min(0).default(0),
    /** Subset billed via the intent-attribution path (rescue panel CTA click). */
    ordersBilledForIntentAttribution: z.number().int().min(0).default(0),
    /**
     * Snapshot of the team's `billIntentAttributedOrdersPercent` setting
     * at window creation. Per-order attribution billing reads this value
     * (not the live team setting) at order-write time, so mid-window
     * setting changes don't retroactively alter what gets billed for the
     * current period — the new value takes effect at the next window
     * cycle. Absent on windows that predate the intent-attribution
     * feature; those windows do not accrue attribution charges.
     */
    billIntentAttributedOrdersPercent: z.number().gt(0).lte(100).optional(),
    /**
     * Sum of attributed-order billable amounts (USD cents) for the period.
     * Equal to sum over attributed orders of `order_subtotal_usd_cents ×
     * billIntentAttributedOrdersPercent / 100`. Drives the period's
     * attribution invoice line item.
     */
    intentAttributionBillableAmountCents: zUsdCents.default(zUsdCents.parse(0)),
    /**
     * Sum of attributed-order subtotals (USD cents) for the period. Stored
     * for analytics / blended-rate display; not used as the billing
     * driver — `intentAttributionBillableAmountCents` is.
     */
    intentAttributionOrdersTotalCents: zUsdCents.default(zUsdCents.parse(0)),
    /**
     * Set by `seed-co-per-order-billing-windows` after it has added the
     * legacy `service_invoice.billableOrdersCreated` snapshot onto this
     * window's counters. Used as the seed's idempotency marker — the
     * seed script's update filters on `seededAt: { $exists: false }`,
     * so re-running on already-seeded windows is a no-op.
     */
    seededAt: z.coerce.date().optional(),
  });
export type CheckoutOptimizationPerOrderBillingWindow = z.infer<
  typeof checkoutOptimizationPerOrderBillingWindowSchema
>;

export type RecoverBillingWindow = z.infer<typeof recoverBillingWindowSchema>;
export const recoverBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.RECOVER),
  revenueSharePercentage: z.number(),
  clawbacksEnabled: z.boolean().nullish().default(false),
  billPerSmsEnabled: z.boolean(),
  includedSmsCredits: z.number(),
  overagePricePerSmsMicros: zUsdMicros,
  overagePricePerMmsMicros: zUsdMicros,
  ignoreCarrierFees: z.boolean(),
});

export type LcgCommitBillingWindow = z.infer<
  typeof lcgCommitBillingWindowSchema
>;
export const lcgCommitBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.LCG_COMMIT),
});

export type DisputesBillingWindow = z.infer<typeof disputesBillingWindowSchema>;
export const disputesBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.DISPUTES),
  // Optional with defaults for backward compatibility with existing documents
  minFeeDollars: z.number().nullish().default(0),
  feePercentage: z.number().nullish().default(25),
  billableFrom: z.coerce.date().nullish(),
});

/**
 * Snapshot of the alerts/deflection enable flags + per-event prices at window
 * creation, so a mid-window change takes effect next cycle, not retroactively.
 */
export type DisputesAlertsDeflectionsBillingWindow = z.infer<
  typeof disputesAlertsDeflectionsBillingWindowSchema
>;
export const disputesAlertsDeflectionsBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.DISPUTES_ALERTS_DEFLECTIONS),
    alertsEnabled: z.boolean().nullish().default(false),
    deflectionEnabled: z.boolean().nullish().default(false),
    alertPriceRdrDollars: z.number().nullish(),
    alertPriceCdrnDollars: z.number().nullish(),
    alertPriceEthocaDollars: z.number().nullish(),
    resolvePriceDollars: z.number().nullish(),
  });

export const billingWindowConfigSchema = z.union([
  checkoutOptimizationBillingWindowSchema,
  checkoutOptimizationPerOrderBillingWindowSchema,
  marketingSmsBillingWindowSchema,
  marketingEmailBillingWindowSchema,
  orderTrackingBillingWindowSchema,
  returnAiExchangeBillingWindowSchema,
  returnsBillingWindowSchema,
  aeoBillingWindowSchema,
  imsBillingWindowSchema,
  imsForecastingBillingWindowSchema,
  recoverBillingWindowSchema,
  disputesBillingWindowSchema,
  disputesAlertsDeflectionsBillingWindowSchema,
  lcgCommitBillingWindowSchema,
  ctxUsageBillingWindowSchema,
  supportAiUsageBillingWindowSchema,
  dimensionalPackagingBillingWindowSchema,
  wmsBillingWindowSchema,
  omsBillingWindowSchema,
]);

export type BillingWindowConfig = z.infer<typeof billingWindowConfigSchema>;
